import { Button, Text } from '@react-spectrum/s2';
import { style } from '@react-spectrum/s2/style' with { type: 'macro' };
import { useEffect, useRef } from 'react';

import { useBridgeApi } from '@/api/BridgeApi';
import { useUserSettings } from '@/hooks/useUserSettings';

import type { InstagramConnectionCard as InstagramConnectionCardMessage } from '../../../../../bridge/types/messages.ts';

import { MessageRow } from './MessageRow';

const cardStyle = style({
	backgroundColor: 'layer-1',
	borderColor: 'gray-300',
	borderRadius: 'lg',
	borderStyle: 'solid',
	borderWidth: 1,
	display: 'flex',
	flexDirection: 'column',
	gap: 12,
	maxWidth: 640,
	padding: 16,
	width: 'full',
});

const headerStyle = style({
	display: 'flex',
	flexDirection: 'column',
	gap: 4,
});

const actionsStyle = style({
	display: 'flex',
	gap: 8,
});

const descriptionStyle = style({
	color: 'gray-700',
	font: 'body',
	fontSize: 'body-sm',
});

export const InstagramConnectionCard = ({
	message,
	chatId,
}: {
	message: InstagramConnectionCardMessage;
	chatId: string;
}) => {
	const bridgeApi = useBridgeApi();
	const {
		clearInstagramConnectFailure,
		connectInstagram,
		instagramConnectFailure,
		isInstagramConnectPending,
		isInstagramConnected,
		settings,
	} = useUserSettings();
	const reportedRef = useRef(false);

	// Reconnect cards are sent while the user is still connected (e.g. missing
	// permission or expiring token), so "is connected" doesn't mean done — the
	// card completes only once the connection is renewed after the card was sent.
	const isReconnect = message.variant === 'reconnect';
	const connectedAt = settings?.instagram_connection?.connected_at;
	const isConnectionRenewed =
		!!connectedAt && new Date(connectedAt) > new Date(message.timestamp);
	const isCompleted =
		message.status === 'completed' ||
		(isReconnect ? isConnectionRenewed : isInstagramConnected);

	// Only the card the user acted on observes the pending→complete transition
	// while mounted. Cards that mount already complete (the connection was made
	// or renewed elsewhere — another thread, the settings page) render as done
	// but stay silent: reporting completion forces an agent turn in this chat,
	// which must only happen where the user actually connected.
	const completedAtMountRef = useRef(isCompleted);

	useEffect(() => {
		if (message.status !== 'pending') return;
		if (completedAtMountRef.current) return;
		if (!isCompleted || reportedRef.current) return;
		reportedRef.current = true;
		bridgeApi.sendAction({
			type: 'report_instagram_connection_card_completed',
			chat_id: chatId,
			request_id: message.request_id,
		});
	}, [bridgeApi, chatId, isCompleted, message.request_id, message.status]);

	// A failure belongs to the card whose Connect opened the popup, and the
	// provider stamps that on it. Matching by request_id replaces the old seed,
	// which read the failure present at mount. The popup outlives this mount --
	// `Chat.tsx` swaps the message list for a Loading node on a thread switch or
	// while a reply arrives -- so the card was routinely unmounted when its own
	// failure landed. Remounting then seeded the guard with that very failure,
	// the effect returned early, and the reason never reached the chat. That is
	// the reported symptom of ADO-1943: the agent answers "I do not know".
	useEffect(() => {
		if (message.status !== 'pending') return;
		if (!instagramConnectFailure) return;
		if (instagramConnectFailure.request_id !== message.request_id) return;
		// Reporting is one-shot: the provider holds the failure for the whole
		// session, and a second report would re-stamp failed_at and make a
		// superseded reason look current.
		clearInstagramConnectFailure();
		bridgeApi.sendAction({
			type: 'report_instagram_connection_card_failed',
			chat_id: chatId,
			request_id: message.request_id,
			step: instagramConnectFailure.step,
			message: instagramConnectFailure.message,
			// Send WHEN it failed, not when this report lands. The report is
			// unbounded, because the card reports on remount and the popup outlives
			// its mount. The agent compares this against the connection time to hide
			// a failure a later connect replaced, so an arrival stamp made a working
			// connection look broken (ADO-1943).
			failed_at: instagramConnectFailure.failed_at,
			...(instagramConnectFailure.details && {
				details: instagramConnectFailure.details,
			}),
		});
	}, [
		bridgeApi,
		chatId,
		clearInstagramConnectFailure,
		instagramConnectFailure,
		message.request_id,
		message.status,
	]);

	const handleConnect = () => {
		if (message.status !== 'pending') return;
		if (isInstagramConnectPending) return;
		connectInstagram(message.request_id);
	};

	return (
		<MessageRow justify="start">
			<div className={cardStyle}>
				<div className={headerStyle}>
					<Text
						styles={style({
							font: 'body',
							fontWeight: 'bold',
							fontSize: 'body',
						})}
					>
						{message.title}
					</Text>
					<Text styles={descriptionStyle}>{message.description}</Text>
				</div>
				<div className={actionsStyle}>
					<Button
						variant="primary"
						onPress={handleConnect}
						isDisabled={isCompleted || isInstagramConnectPending}
					>
						{isReconnect
							? isCompleted
								? 'Reconnected'
								: 'Reconnect'
							: isCompleted
								? 'Connected'
								: 'Connect'}
					</Button>
				</div>
			</div>
		</MessageRow>
	);
};
